import classNames from "classnames";
import {
  GetPhoneOptionsResponse,
  PhoneInactivationOption,
  PhonesInactivationSource,
  ValidationErrors,
} from "../../../lib/api/models";
import styles from "./ModifyPhoneActivationStatus.module.css";
import Toggle from "../../common/Toggle";
import Select from "../../common/inputs/Select";
import LabeledInfo from "../../common/LabeledInfo";
import {
  formatInCentralTime,
  formatToDateStringInCentralTime,
} from "../../../lib/formatting";
import StyledRadioButton from "../../common/inputs/StyledRadioButton";
import DateInput from "../../common/inputs/DateInput";
import { PhoneNumberFormInput } from "../PhoneNumber.hooks";
import ValidationMessage from "../../common/ValidationMessage/ValidationMessage";
import { addDays } from "date-fns";
import { assertIsDefined } from "../../../lib/assertions";

const getReactivationDate = (inactivationReason: PhoneInactivationOption) => {
  switch (inactivationReason.defaultReactivationDate) {
    case -1:
      return null;
    case 0:
      return "";
    default:
      return formatToDateStringInCentralTime(
        addDays(new Date(), inactivationReason.defaultReactivationDate),
      );
  }
};

const getInactivationSourceType = (
  inactivationReason: PhoneInactivationOption,
) => {
  const filtered = inactivationReason.inactivationSourceTypes.filter(
    (o) => o.selectable,
  );
  switch (filtered.length) {
    case 0:
      return null;
    case 1:
      return filtered[0].id;
    default:
      return undefined;
  }
};

const getInactivationDeliveryType = (
  inactivationReason: PhoneInactivationOption,
) => {
  const filtered = inactivationReason.inactivationDeliveryTypes.filter(
    (o) => o.selectable,
  );
  switch (filtered.length) {
    case 0:
      return null;
    case 1:
      return filtered[0].id;
    default:
      return undefined;
  }
};

const MAX_DATE_RANGE = "9999-12-31";

const strings = {
  status: "Status: ",
  active: "Active",
  inactive: "Inactive",
  inactivationReason: "Inactivation Reason",
  inactivationDate: "Inactivation Date",
  reactivationDate: "Reactivation Date",
  reactivationDateAriaLabel: "Date input for reactivation date",
  requestedBy: "Requested By",
  typeOfRequest: "Type of Request",
  select: "Select",
  never: "Never",
};

type Props = {
  formState: PhoneNumberFormInput;
  generalDispatch: (payload: Partial<PhoneNumberFormInput>) => void;
  validationMessages?: ValidationErrors;
  options: GetPhoneOptionsResponse;
  disabled?: boolean;
};

export default function ModifyPhoneActivationStatus({
  formState: {
    active,
    inactivationReasonId,
    reactivationDate,
    inactivationSourceType,
    inactivationDeliveryType,
    consentStatus,
  },
  generalDispatch,
  validationMessages,
  options,
  disabled,
}: Props) {
  const currInactivationReason = options.inactivationReasons.find(
    (o) => o.id === inactivationReasonId,
  );

  return (
    <div
      className={classNames(
        styles.container,
        styles.fontSize,
        active ? styles.greenBackground : styles.yellowBackground,
      )}
    >
      <Toggle
        checked={active}
        onChange={(e) => {
          generalDispatch({
            active: e.target.checked,
            consentStatus:
              active &&
              options.inactivationReasons.find(
                (o) => o.id === inactivationReasonId,
              )?.consentRevoke
                ? "PEC_NO"
                : consentStatus,
            inactivationReasonId: undefined,
            reactivationDate: null,
            inactivationDeliveryType: undefined,
            inactivationSourceType: undefined,
          });
        }}
        label={
          <div className={styles.statusLine}>
            <span className={styles.bold}>{strings.status}</span>
            <div className={active ? styles.activeBadge : styles.inactiveBadge}>
              {active ? strings.active : strings.inactive}
            </div>
          </div>
        }
        aria-label={active ? strings.active : strings.inactive}
        disabled={disabled}
      />

      {!active && (
        <div className={styles.content}>
          <Select
            labelClassName={styles.fontSize}
            label={strings.inactivationReason}
            placeholder={strings.select}
            selectedValue={inactivationReasonId}
            onChange={(v) => {
              const inactivationReason = options.inactivationReasons.find(
                (o) => o.id === v,
              );
              assertIsDefined(inactivationReason);

              generalDispatch({
                inactivationReasonId: v,
                reactivationDate: getReactivationDate(inactivationReason),
                inactivationSourceType:
                  getInactivationSourceType(inactivationReason),
                inactivationDeliveryType:
                  getInactivationDeliveryType(inactivationReason),
                consentStatus: inactivationReason?.consentRevoke
                  ? "PEC_NO"
                  : consentStatus,
              });
            }}
            options={options.inactivationReasons
              .filter((o) => o.selectable)
              .map((o) => ({
                key: o.id,
                value: o.id,
                label: o.value,
              }))}
            validationMessages={validationMessages?.inactivationReasonId}
            disabled={disabled}
          />
          <LabeledInfo
            label={strings.inactivationDate}
            value={formatInCentralTime(new Date(), "MM/dd/yyyy")}
            orientation="vertical"
          />
          <div className={styles.spaceApartColumn}>
            {strings.reactivationDate}
            {currInactivationReason?.defaultReactivationDate !== -1 ? (
              <StyledRadioButton
                className={styles.dateRadioButton}
                label={
                  <DateInput
                    inputClassName={styles.noMargin}
                    aria-label={strings.reactivationDate}
                    value={reactivationDate ?? ""}
                    onChangeDate={(v) =>
                      generalDispatch({
                        reactivationDate: v,
                      })
                    }
                    max={MAX_DATE_RANGE}
                  />
                }
                aria-label={strings.reactivationDateAriaLabel}
                checked={reactivationDate !== null}
                onChange={() => generalDispatch({ reactivationDate: "" })}
                disabled={disabled}
              />
            ) : null}
            <StyledRadioButton
              label={strings.never}
              className={styles.bold}
              checked={reactivationDate === null}
              onChange={() =>
                generalDispatch({
                  reactivationDate: null,
                })
              }
              disabled={disabled}
            />
            {validationMessages?.reactivationDate && (
              <ValidationMessage
                validationMessages={validationMessages.reactivationDate}
              />
            )}
          </div>
          {currInactivationReason?.inactivationSourceTypes.filter(
            (o) => o.selectable,
          ).length ? (
            <Select
              label={strings.requestedBy}
              placeholder={strings.select}
              selectedValue={inactivationSourceType ?? undefined}
              onChange={(v: PhonesInactivationSource) =>
                generalDispatch({
                  inactivationSourceType: v,
                })
              }
              disabled={!inactivationReasonId || disabled}
              options={
                currInactivationReason?.inactivationSourceTypes
                  .filter((o) => o.selectable)
                  .map((o) => ({
                    key: o.id,
                    value: o.id,
                    label: o.value,
                  })) ?? []
              }
              validationMessages={validationMessages?.inactivationSourceType}
            />
          ) : null}
          <div className={styles.typeOfRequest}>
            {currInactivationReason?.inactivationDeliveryTypes.filter(
              (o) => o.selectable,
            ).length ? (
              <>
                {strings.typeOfRequest}
                <div>
                  {currInactivationReason?.inactivationDeliveryTypes
                    .filter((o) => o.selectable)
                    .map((o) => (
                      <StyledRadioButton
                        key={o.id}
                        label={o.value}
                        checked={inactivationDeliveryType === o.id}
                        onChange={() =>
                          generalDispatch({
                            inactivationDeliveryType: o.id,
                          })
                        }
                        disabled={disabled}
                      />
                    ))}
                </div>
              </>
            ) : null}
            {validationMessages?.inactivationDeliveryType && (
              <ValidationMessage
                validationMessages={validationMessages.inactivationDeliveryType}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
